/**
 * Right-edge user widget in the navy top bar (XL+ only).
 *
 * Two modes:
 *   - Unauthenticated: red "Sign In" button with a hover dropdown listing
 *     the auction and Stock sign-ins, as on the corporate header.
 *   - Authenticated: shows the customer's display name with a hover
 *     dropdown linking to Dashboard / Account settings and a Sign out
 *     action (callback supplied by AppShell).
 *
 * The mobile equivalents live in MobileNav.tsx — those branches use the
 * same `user` prop shape so the two implementations stay in sync.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SIGNIN_HREF, STOCK_LOGIN_HREF } from './chromeNav';

export interface TopBarUser {
  name?: string | null;
  email?: string | null;
  onSignOut?: () => void;
}

interface Props {
  user?: TopBarUser;
  signInLabel?: string;
  auctionLabel?: string;
  stockLabel?: string;
  accountLabel?: string;
  signOutLabel?: string;
  dashboardLabel?: string;
}

export function TopBarSignIn({
  user,
  signInLabel = 'Sign In',
  auctionLabel = 'Auction',
  stockLabel = 'Stock',
  accountLabel = 'Account settings',
  signOutLabel = 'Sign out',
  dashboardLabel = 'Dashboard',
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);

  if (user) {
    const displayName = user.name || user.email || accountLabel;
    return (
      <div
        className="relative"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          type="button"
          className="text-sm font-medium text-white/90 hover:text-white px-3 py-1.5 rounded transition-colors duration-150 flex items-center gap-1.5"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className="max-w-[180px] truncate">{displayName}</span>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open ? (
          <div className="absolute top-full right-0 pt-1 z-50">
            <div className="bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[200px]">
              <Link
                to="/orders"
                className="block px-4 py-2 text-sm text-foreground-secondary hover:text-foreground hover:bg-brand-50 transition-colors duration-150"
                onClick={() => setOpen(false)}
              >
                {dashboardLabel}
              </Link>
              <Link
                to="/account"
                className="block px-4 py-2 text-sm text-foreground-secondary hover:text-foreground hover:bg-brand-50 transition-colors duration-150"
                onClick={() => setOpen(false)}
              >
                {accountLabel}
              </Link>
              {user.onSignOut ? (
                <button
                  type="button"
                  onClick={() => {
                    user.onSignOut?.();
                    setOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-foreground-secondary hover:text-foreground hover:bg-brand-50 transition-colors duration-150 border-t border-slate-100"
                >
                  {signOutLabel}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="text-sm font-semibold text-white bg-red-600 hover:bg-red-500 px-4 py-1.5 rounded transition-colors duration-150 flex items-center gap-1"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {signInLabel}
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open ? (
        <div className="absolute top-full right-0 pt-1 z-50">
          <div className="bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[180px]">
            <a
              href={SIGNIN_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-4 py-2 text-sm text-foreground-secondary hover:text-foreground hover:bg-brand-50 transition-colors duration-150"
            >
              {auctionLabel}
            </a>
            <a
              href={STOCK_LOGIN_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-4 py-2 text-sm text-foreground-secondary hover:text-foreground hover:bg-brand-50 transition-colors duration-150"
            >
              {stockLabel}
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}
